// React Dependencies
import React, { useEffect, useReducer } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';

// Internal Dependencies
import MText from '../../../../../components/Common/MText';
import Colours from '../../../../../styles/colours';
import useResized from '../../../../../common/use-resized';
import withLoyaltyConnect from '../WithLoyaltyConnect';
import { IsEmpty, IsTrue } from '../../../../../common/checks';
import { VoucherStyles, VoucherImages } from '../constants';

const useStyles = makeStyles({
  previewContainer: {
    paddingBottom:               28,
    border:                      `1px solid ${Colours.Gray8}`,
    borderRadius:                12,
    '@media (min-width: 992px)': {
      paddingBottom: 36,
    },
  },
});

const getPreviewedVoucher = (voucherList) => {
  if (IsEmpty(voucherList)) {
    return null;
  }
  if (voucherList.length === 1) {
    return voucherList[0];
  }
  const priorities = [
    (v) => v.balQty > 0 && IsEmpty(v.issuableQty),
    (v) => v.balQty > 0,
    (v) => IsEmpty(v.issuableQty),
  ];
  for (const condition of priorities) {
    const match = voucherList.find(condition);
    if (match) {
      return match;
    }
  }
  return voucherList[voucherList.length - 1];
};

const PreviewGift = (props) => {
  const classes = useStyles();
  const [resizedRef, imageSize] = useResized();

  const [relativeSizes, setRelativeSizes] = useReducer(
    (state, action) => ({ ...state, ...action }),
    {
      voucherTitle:    0,
      voucherSubtitle: 0,
      pageLogo:        0,
      product:         0,
    },
  );

  const { selectedVoucherId } = props.loyaltyProgram;

  const previewedVoucher = !IsEmpty(selectedVoucherId)
    ? props.loyaltyProgram?.vouchers.find((voucher) => voucher.id === selectedVoucherId)
    : getPreviewedVoucher(props.loyaltyProgram?.vouchers);

  const currentDesign = !IsEmpty(previewedVoucher?.design)
    ? previewedVoucher.design
    : VoucherImages.Green;

  const fontColor = !IsEmpty(currentDesign)
    ? VoucherStyles[currentDesign].fontColor
    : Colours.Black;

  useEffect(() => {
    setRelativeSizes({
      voucherTitle:    imageSize.height * 0.166,
      voucherSubtitle: imageSize.height * 0.093,
      pageLogo:        imageSize.height * 0.63,
      product:         imageSize.height * 0.332,
    });
  }, [imageSize]);

  return (
    <div>
      <div className={`w-100 px-md-2 px-xl-3 pt-1 pt-lg-3 user-select-none ${classes.previewContainer}`}>
        <MText text="Voucher Preview" className="text-center pb-1 pb-lg-3" bold size={16} />
        <div
          className="mx-3"
          style={{
            visibility: IsEmpty(props.loyaltyProgram?.vouchers) || IsEmpty(previewedVoucher.design)
              ? 'hidden'
              : 'visible',
          }}
        >
          <div
            className="w-100 position-relative overflow-hidden"
            style={{ paddingTop: 'calc((46 / 80) * 100%)' }}
          >
            <img
              ref={resizedRef}
              alt="voucher"
              src={previewedVoucher?.design}
              className="w-100 position-absolute"
              style={{ top: 0, bottom: 0, height: '100%', width: '100%', objectFit: 'contain' }}
            />
            <div className="position-absolute d-flex" style={{ top: '30%', left: '64%' }}>
              <img
                alt="product"
                className="rounded-circle"
                width={relativeSizes.product}
                height={relativeSizes.product}
                style={{ objectFit: 'cover', display: IsTrue(previewedVoucher?.isProduct) ? 'block' : 'none' }}
                src={
                  (!IsEmpty(previewedVoucher?.productImage) && IsTrue(previewedVoucher?.isProduct))
                    ? previewedVoucher.productImage
                    : '/images/no-image.png'
                }
              />
            </div>
            <div className="position-absolute d-flex" style={{ top: '12%', left: '50%', transform: 'translateX(-50%)' }}>
              <img
                alt="page-logo"
                className="rounded-circle"
                width={relativeSizes.pageLogo}
                height={relativeSizes.pageLogo}
                style={{ objectFit: 'cover' }}
                src={!IsEmpty(props.page?.pageLogo)
                  ? props.page?.pageLogo
                  : '/images/no-image.png'}
              />
            </div>
            <div className="position-absolute d-flex flex-column align-items-center w-100" style={{ bottom: '1%' }}>
              <MText
                size={relativeSizes.voucherTitle}
                colour={fontColor}
                className="text-truncate"
                text={previewedVoucher?.label}
                style={{
                  fontFamily: 'Concept-Bold',
                  lineHeight: '90%',
                  textAlign:  'center',
                  maxWidth:   '85%',
                }}
              />
              <MText
                size={relativeSizes.voucherSubtitle}
                colour={fontColor}
                style={{ fontFamily: 'Optima-Regular', marginTop: '3%', transform: 'translateY(-55%)', textAlign: 'center' }}
                text={IsEmpty(previewedVoucher?.minSpend)
                  ? 'No Minimum Spend'
                  : `Minimum $${previewedVoucher.minSpend} Spend`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PreviewGift.propTypes = {
  loyaltyProgram: PropTypes.shape({
    selectedVoucherId: PropTypes.string,
    name:              PropTypes.string,
    image:             PropTypes.string,
    vouchers:          PropTypes.arrayOf(PropTypes.shape({
      label:        PropTypes.string,
      design:       PropTypes.string,
      minSpend:     PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      isProduct:    PropTypes.bool,
      productImage: PropTypes.string,
    })),
  }).isRequired,
  page: PropTypes.shape({
    pageLogo: PropTypes.string,
  }).isRequired,
};

export default withLoyaltyConnect(PreviewGift);
